import { assertUnreachable, FormatedFeatureType, VisibleFeatureType } from "shared";

export const featureCategories = [
  "Folding",
  "Hole",
  "Side",
] as const satisfies readonly VisibleFeatureType[];

export const holeSubTypes = [
  "hole_circular",
  "hole_slotted",
  "hole_complex",
  "hole_folded",
] as const;

export type HoleSubType = (typeof holeSubTypes)[number];

export type AddFeatureFormState = {
  type: VisibleFeatureType;
  holeSubType: HoleSubType;
  angle: number | null;
  diameter: number | null;
  edgeLength: number | null;
  length: number | null;
  count: number | null;
};

export const DEFAULT_ADD_FEATURE_FORM: AddFeatureFormState = {
  type: "Folding",
  holeSubType: "hole_circular",
  angle: null,
  diameter: null,
  edgeLength: null,
  length: null,
  count: null,
};

export const usesDiameter = (subType: HoleSubType) =>
  subType === "hole_circular" || subType === "hole_slotted";

export const usesPerimeter = (subType: HoleSubType) =>
  subType === "hole_complex" || subType === "hole_folded";

export const changeHoleSubType = (
  feature: Extract<FormatedFeatureType, { type: "Hole" }>,
  subType: HoleSubType,
): FormatedFeatureType => {
  const count = feature.count;

  switch (subType) {
    case "hole_circular":
    case "hole_slotted":
      return {
        type: "Hole",
        subType,
        count,
        diameter: "diameter" in feature ? feature.diameter : 0,
      };
    case "hole_complex":
    case "hole_folded":
      return {
        type: "Hole",
        subType,
        count,
        edgeLength: "edgeLength" in feature ? feature.edgeLength : 0,
      };
    default:
      return assertUnreachable(subType);
  }
};

export const buildFeatureFromAddForm = (form: AddFeatureFormState): FormatedFeatureType | null => {
  if (form.count === null || form.count < 1) {
    return null;
  }

  switch (form.type) {
    case "Folding":
      return {
        type: "Folding",
        count: form.count,
        angle: form.angle,
      };
    case "Side":
      if (form.length === null) {
        return null;
      }

      return {
        type: "Side",
        count: form.count,
        length: form.length,
      };
    case "Hole":
      return buildHoleFromAddForm(form);
    default:
      return assertUnreachable(form.type);
  }
};

const buildHoleFromAddForm = (form: AddFeatureFormState): FormatedFeatureType | null => {
  if (form.count === null || form.count < 1) {
    return null;
  }

  switch (form.holeSubType) {
    case "hole_circular":
    case "hole_slotted":
      if (form.diameter === null) {
        return null;
      }

      return {
        type: "Hole",
        subType: form.holeSubType,
        count: form.count,
        diameter: form.diameter,
      };
    case "hole_complex":
    case "hole_folded":
      if (form.edgeLength === null) {
        return null;
      }

      return {
        type: "Hole",
        subType: form.holeSubType,
        count: form.count,
        edgeLength: form.edgeLength,
      };
    default:
      return assertUnreachable(form.holeSubType);
  }
};
